import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Phone, Download, Play, Pause, Clock, TrendingUp, AlertCircle, CheckCircle, Loader2, ChevronDown, ChevronUp, BarChart3, Filter, Settings2, Settings } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { CallActionsMenu } from "@/components/admin/CallActionsMenu";
import { CallFilters } from "@/components/admin/CallFilters";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface PhoneLead {
  id: string;
  phone_number: string;
  caller_phone: string | null;
  page_url: string | null;
  page_title: string | null;
  created_at: string;
  status: string;
  call_status: string | null;
  call_duration: number | null;
  recording_url: string | null;
  transcription: string | null;
  twilio_call_sid: string | null;
  dial_call_status: string | null;
  lead_status: string;
  priority: string;
  call_type: string | null;
  lead_quality: string | null;
  service_interest: string | null;
  customer_name: string | null;
  customer_email: string | null;
  estimated_value: number | null;
  follow_up_date: string | null;
}

interface AdminCallsProps {
  voiceSettingsOpen?: boolean;
  onVoiceSettingsOpenChange?: (open: boolean) => void;
}

const AdminCalls = ({ voiceSettingsOpen, onVoiceSettingsOpenChange }: AdminCallsProps = {}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [calls, setCalls] = useState<PhoneLead[]>([]);
  const [filteredCalls, setFilteredCalls] = useState<PhoneLead[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [leadStatusFilter, setLeadStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [leadQualityFilter, setLeadQualityFilter] = useState("all");
  const [callTypeFilter, setCallTypeFilter] = useState("all");
  const [playingAudio, setPlayingAudio] = useState<string | null>(null);
  const [loadingAudio, setLoadingAudio] = useState<string | null>(null);
  const [audioError, setAudioError] = useState<string | null>(null);
  const [statsOpen, setStatsOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate("/admin/login");
    }
  }, [user, navigate]);

  useEffect(() => {
    loadCalls();
  }, []);

  useEffect(() => {
    filterCalls();
  }, [calls, searchTerm, leadStatusFilter, priorityFilter, leadQualityFilter, callTypeFilter]);

  const loadCalls = async () => {
    const { data, error } = await supabase
      .from('phone_leads')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error loading calls:', error);
      toast.error("Failed to load calls");
    } else {
      setCalls(data || []);
    }
  };

  const filterCalls = () => {
    let filtered = calls;

    if (searchTerm) {
      filtered = filtered.filter(call =>
        call.phone_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        call.caller_phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        call.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        call.customer_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        call.service_interest?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (leadStatusFilter !== "all") {
      filtered = filtered.filter(call => call.lead_status === leadStatusFilter);
    }

    if (priorityFilter !== "all") {
      filtered = filtered.filter(call => call.priority === priorityFilter);
    }

    if (leadQualityFilter !== "all") {
      filtered = filtered.filter(call => call.lead_quality === leadQualityFilter);
    }

    if (callTypeFilter !== "all") {
      filtered = filtered.filter(call => call.call_type === callTypeFilter);
    }

    setFilteredCalls(filtered);
  };

  const stats = {
    totalCalls: filteredCalls.length,
    newLeads: filteredCalls.filter(c => c.lead_status === 'new').length,
    qualified: filteredCalls.filter(c => c.lead_status === 'qualified').length,
    converted: filteredCalls.filter(c => c.lead_status === 'converted').length,
    urgentPriority: filteredCalls.filter(c => c.priority === 'urgent').length,
    hotLeads: filteredCalls.filter(c => c.lead_quality === 'hot').length,
    estimatedValue: filteredCalls.reduce((sum, c) => sum + (c.estimated_value || 0), 0),
    avgDuration: filteredCalls.filter(c => c.call_duration).length > 0
      ? Math.round(filteredCalls.reduce((sum, c) => sum + (c.call_duration || 0), 0) / filteredCalls.filter(c => c.call_duration).length)
      : 0
  };

  const handleStatusUpdate = async (callId: string, status: string) => {
    const { error } = await supabase
      .from('phone_leads')
      .update({ lead_status: status })
      .eq('id', callId);

    if (error) {
      toast.error("Failed to update status");
    } else {
      toast.success("Status updated successfully");
      loadCalls();
    }
  };

  const handlePriorityUpdate = async (callId: string, priority: string) => {
    const { error } = await supabase
      .from('phone_leads')
      .update({ priority })
      .eq('id', callId);

    if (error) {
      toast.error("Failed to update priority");
    } else {
      toast.success("Priority updated successfully");
      loadCalls();
    }
  };

  const getLeadStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      new: "bg-blue-500",
      contacted: "bg-purple-500",
      qualified: "bg-green-500",
      unqualified: "bg-red-500",
      converted: "bg-emerald-600",
      no_answer: "bg-gray-500",
      callback_scheduled: "bg-yellow-500",
      lost: "bg-red-700"
    };

    return (
      <Badge className={colors[status]}>
        {status.replace('_', ' ')}
      </Badge>
    );
  };

  const getPriorityBadge = (priority: string) => {
    const colors: Record<string, string> = {
      urgent: "bg-red-500",
      high: "bg-orange-500",
      medium: "bg-yellow-500",
      low: "bg-gray-500"
    };

    return (
      <Badge className={colors[priority]}>
        {priority}
      </Badge>
    );
  };

  const getLeadQualityBadge = (quality: string | null) => {
    if (!quality) return null;
    
    const colors: Record<string, string> = {
      hot: "bg-red-600",
      warm: "bg-orange-500",
      cold: "bg-blue-500",
      unqualified: "bg-gray-500"
    };

    return (
      <Badge className={colors[quality]}>
        {quality}
      </Badge>
    );
  };

  const getCallOutcomeBadge = (call: PhoneLead) => {
    const callStatus = call.call_status;
    const dialCallStatus = call.dial_call_status;
    const hasRecording = !!call.recording_url;
    
    // Answered call - DialCallStatus explicitly says 'completed'
    if (dialCallStatus === 'completed') {
      return (
        <Badge className="bg-green-600 text-white">
          ✓ Answered
        </Badge>
      );
    }
    
    // Voicemail - Dial failed but we have a recording (customer left message)
    if (hasRecording && dialCallStatus !== 'completed' && callStatus === 'completed') {
      return (
        <Badge className="bg-purple-600 text-white">
          📧 Voicemail
        </Badge>
      );
    }
    
    // No answer - Dial failed, no recording left
    if (dialCallStatus === 'no-answer' || (callStatus === 'no-answer' && !hasRecording)) {
      return (
        <Badge className="bg-yellow-600 text-white">
          📞 No Answer
        </Badge>
      );
    }
    
    // Busy
    if (dialCallStatus === 'busy' || callStatus === 'busy') {
      return (
        <Badge className="bg-orange-600 text-white">
          🔴 Busy
        </Badge>
      );
    }
    
    // Failed or canceled
    if (dialCallStatus === 'failed' || callStatus === 'failed' || callStatus === 'canceled') {
      return (
        <Badge className="bg-red-600 text-white">
          ✗ Failed
        </Badge>
      );
    }
    
    // Default - in progress or unknown
    return (
      <Badge className="bg-gray-500 text-white">
        ⏳ In Progress
      </Badge>
    );
  };

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return "N/A";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePlayRecording = async (callId: string) => {
    if (playingAudio === callId) {
      setPlayingAudio(null);
      const audio = document.getElementById(`audio-${callId}`) as HTMLAudioElement;
      audio?.pause();
    } else {
      setLoadingAudio(callId);
      setAudioError(null);
      const audio = document.getElementById(`audio-${callId}`) as HTMLAudioElement;
      
      try {
        await audio?.play();
        setPlayingAudio(callId);
        setLoadingAudio(null);
      } catch (error) {
        console.error('Error playing audio:', error);
        setAudioError(callId);
        setLoadingAudio(null);
        toast.error('Failed to play recording. Please try downloading instead.');
      }
    }
  };

  const getProxyRecordingUrl = (callSid: string) => {
    return `https://ltugnhpocjzhouvvciek.supabase.co/functions/v1/proxy-recording?callSid=${callSid}`;
  };

  const activeFiltersCount = [
    searchTerm,
    leadStatusFilter !== "all" && leadStatusFilter,
    priorityFilter !== "all" && priorityFilter,
    leadQualityFilter !== "all" && leadQualityFilter,
    callTypeFilter !== "all" && callTypeFilter,
  ].filter(Boolean).length;

  return (
    <div className="space-y-3 md:space-y-4">
      {/* Compact 3-Button Row: Stats, Filters, Settings */}
      <div className="flex gap-2">
        {/* Stats Button */}
        <Button 
          variant="outline" 
          onClick={() => setStatsOpen(!statsOpen)}
          className="flex-1 h-9"
        >
          <BarChart3 className="h-4 w-4" />
          <span className="hidden sm:inline ml-2">Stats</span>
          {statsOpen && <ChevronUp className="h-4 w-4 ml-auto" />}
          {!statsOpen && <ChevronDown className="h-4 w-4 ml-auto" />}
        </Button>
        
        {/* Filters Button */}
        <Button 
          variant="outline" 
          onClick={() => setFiltersOpen(!filtersOpen)}
          className="flex-1 h-9"
        >
          <Filter className="h-4 w-4" />
          <span className="hidden sm:inline ml-2">Filters{activeFiltersCount > 0 && ` (${activeFiltersCount})`}</span>
          {filtersOpen && <ChevronUp className="h-4 w-4 ml-auto" />}
          {!filtersOpen && <ChevronDown className="h-4 w-4 ml-auto" />}
        </Button>
        
        {/* Settings Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="flex-1 h-9">
              <Settings2 className="h-4 w-4" />
              <span className="hidden sm:inline ml-2">Settings</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem asChild>
              <Link to="/admin/phone-numbers" className="flex items-center gap-2 cursor-pointer">
                <Phone className="h-4 w-4" />
                Phone Numbers
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => onVoiceSettingsOpenChange?.(true)} 
              className="flex items-center gap-2"
            >
              <Settings className="h-4 w-4" />
              Voice Settings
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Collapsible Stats Section */}
      {statsOpen && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          <Card>
            <CardHeader className="pb-2 md:pb-3">
              <CardTitle className="text-xs md:text-sm font-medium">Total Calls</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xl md:text-2xl font-bold">{stats.totalCalls}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2 md:pb-3">
              <CardTitle className="text-xs md:text-sm font-medium flex items-center gap-1 md:gap-2">
                <AlertCircle className="h-3 w-3 md:h-4 md:w-4" />
                <span className="hidden sm:inline">Hot Leads / Urgent</span>
                <span className="sm:hidden">Hot/Urgent</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xl md:text-2xl font-bold">{stats.hotLeads} / {stats.urgentPriority}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2 md:pb-3">
              <CardTitle className="text-xs md:text-sm font-medium flex items-center gap-1 md:gap-2">
                <CheckCircle className="h-3 w-3 md:h-4 md:w-4" />
                <span className="hidden sm:inline">Qualified / Converted</span>
                <span className="sm:hidden">Qual/Conv</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xl md:text-2xl font-bold">{stats.qualified} / {stats.converted}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2 md:pb-3">
              <CardTitle className="text-xs md:text-sm font-medium flex items-center gap-1 md:gap-2">
                <TrendingUp className="h-3 w-3 md:h-4 md:w-4" />
                Est. Value
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xl md:text-2xl font-bold">${stats.estimatedValue.toLocaleString()}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Collapsible Filters Section */}
      {filtersOpen && (
        <Card>
          <CardContent className="p-4 md:p-6">
            <CallFilters
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              leadStatus={leadStatusFilter}
              onLeadStatusChange={setLeadStatusFilter}
              priority={priorityFilter}
              onPriorityChange={setPriorityFilter}
              leadQuality={leadQualityFilter}
              onLeadQualityChange={setLeadQualityFilter}
              callType={callTypeFilter}
              onCallTypeChange={setCallTypeFilter}
            />
          </CardContent>
        </Card>
      )}

      <div className="grid gap-3 md:gap-4">
        {filteredCalls.map((call) => (
          <Card key={call.id}>
            <CardHeader className="p-4 md:p-6">
              <div className="flex justify-between items-start gap-2">
                <div className="space-y-1 flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <CardTitle className="text-base md:text-lg truncate">
                      <Phone className="inline h-4 w-4 mr-2" />
                      {call.caller_phone || call.phone_number}
                    </CardTitle>
                    {getCallOutcomeBadge(call)}
                    {(call.priority === 'urgent' || call.priority === 'high') && getPriorityBadge(call.priority)}
                    {(call.lead_quality === 'hot' || call.lead_quality === 'warm') && getLeadQualityBadge(call.lead_quality)}
                    {call.lead_status !== 'new' && getLeadStatusBadge(call.lead_status)}
                  </div>
                  <CardDescription className="text-xs md:text-sm">
                    {format(new Date(call.created_at), 'PPpp')}
                  </CardDescription>
                  {call.customer_name && (
                    <p className="text-sm md:text-base font-medium truncate">{call.customer_name}</p>
                  )}
                  {call.service_interest && (
                    <p className="text-xs md:text-sm text-muted-foreground truncate">Interest: {call.service_interest}</p>
                  )}
                </div>
                <CallActionsMenu
                  callId={call.id}
                  callData={call}
                  onStatusUpdate={handleStatusUpdate}
                  onPriorityUpdate={handlePriorityUpdate}
                  onRefresh={loadCalls}
                />
              </div>
            </CardHeader>
            <CardContent className="p-4 md:p-6 md:pt-0 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 text-sm">
                <div>
                  <span className="font-medium">Call Duration:</span>{' '}
                  <Clock className="inline h-3 w-3 mr-1" />
                  {formatDuration(call.call_duration)}
                </div>
                {call.call_type && (
                  <div>
                    <span className="font-medium">Type:</span> {call.call_type.replace('_', ' ')}
                  </div>
                )}
                {call.estimated_value && (
                  <div>
                    <span className="font-medium">Est. Value:</span> ${call.estimated_value.toLocaleString()}
                  </div>
                )}
                {call.follow_up_date && (
                  <div>
                    <span className="font-medium">Follow-up:</span>{' '}
                    {format(new Date(call.follow_up_date), 'MMM d, yyyy')}
                  </div>
                )}
              </div>

              {call.page_title && (
                <div className="text-sm">
                  <span className="font-medium">From Page:</span> {call.page_title}
                </div>
              )}

              {call.recording_url && call.twilio_call_sid && (
                <div className="space-y-2 pt-2 border-t">
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handlePlayRecording(call.id)}
                      disabled={loadingAudio === call.id}
                      className="min-h-[44px]"
                    >
                      {loadingAudio === call.id ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading...</>
                      ) : playingAudio === call.id ? (
                        <><Pause className="h-4 w-4 mr-2" /> Pause</>
                      ) : (
                        <><Play className="h-4 w-4 mr-2" /> Play Recording</>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => window.open(getProxyRecordingUrl(call.twilio_call_sid!), '_blank')}
                      className="min-h-[44px]"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                  </div>
                  {audioError === call.id && (
                    <p className="text-sm text-destructive">
                      Unable to play recording. Try downloading instead.
                    </p>
                  )}
                  <audio
                    id={`audio-${call.id}`}
                    src={getProxyRecordingUrl(call.twilio_call_sid)}
                    onEnded={() => setPlayingAudio(null)}
                    onError={() => {
                      setAudioError(call.id);
                      setLoadingAudio(null);
                    }}
                    onLoadedData={() => setLoadingAudio(null)}
                    preload="none"
                  />
                </div>
              )}

              {call.transcription && (
                <div className="mt-2 p-3 bg-muted rounded-lg">
                  <p className="text-sm font-medium mb-1">Transcription:</p>
                  <p className="text-sm">{call.transcription}</p>
                </div>
              )}
            </CardContent>
          </Card>
        ))}

        {filteredCalls.length === 0 && (
          <Card>
            <CardContent className="text-center py-8">
              <p className="text-muted-foreground">No calls found matching your filters</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminCalls;